import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withRouter } from '../../common/withRouter';
import { Link } from 'react-router';
import { getFormValues } from 'redux-form';
import { MotifTabControl, MotifTabNavigation } from '@ey-xd/motif-react';
import * as authActions from '../../actions/authActions';
import PublishDataHeader from '../../components/analytics/PublishDataHeader';
import PublishDataTable from '../../components/analytics/PublishDataTable';
import * as publishDataActions from '../../actions/publishDataActions';

/**
 * PublishDataContainer component for Analytics Reports - Publish Data functionality
 * @extends {React.Component}
 */
export class PublishDataContainer extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      selectedPeriod: null,
      reportPeriods: [],
      selectedReits: {
        testResults: [],
        psq: [],
        arqc: [],
      },
      publishData: [],
      defaultReportPeriod: null,
      isSelectionChanged: false,
    };

    this.handlePeriodChange = this.handlePeriodChange.bind(this);
    this.handleSelectionChange = this.handleSelectionChange.bind(this);
    this.handleCancel = this.handleCancel.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.loadPublishData = this.loadPublishData.bind(this);
    this.isAdminUser = this.isAdminUser.bind(this);
  }

  componentDidMount() {
    this.loadPublishData();
  }

  /**
   * Determines if the current user is a System Administrator or Engagement Administrator
   * @returns {boolean} True if user is an admin, otherwise false
   */
  isAdminUser() {
    const { currentUserAuthorizations, clientId } = this.props;
    if (!Array.isArray(currentUserAuthorizations)) {
      return false;
    }
    const isSysAdmin = authActions.isSystemAdministrator(
      currentUserAuthorizations
    );
    const isEngAdmin = authActions.isEngagementAdministrator(
      currentUserAuthorizations,
      clientId
    );
    return isSysAdmin || isEngAdmin;
  }

  componentWillUnmount() {
    // cleanup if needed
  }

  /**
   * Load publish data for the selected report period
   */
  loadPublishData(reportPeriodDescription = null) {
    this.props.publishDataActions
      .fetchPublicationData(this.props.clientId, reportPeriodDescription)
      .then(() => {
        // get reportPeriodDescription values, filter falsy, and keep distinct while preserving order
        const allPeriods = Array.isArray(this.props.publishData)
          ? this.props.publishData.map((rp) => rp && rp.reportPeriodDescription)
          : [];

        const distinctPeriods = [...new Set(allPeriods.filter(Boolean))];

        const defaultPeriod =
          distinctPeriods.length > 0 ? distinctPeriods[0] : null;

        // If reportPeriodDescription is null, use distinctPeriods for dropdown (meaning it is the initial load), otherwise preserve current state
        let reportPeriods =
          reportPeriodDescription === null
            ? distinctPeriods
            : this.state.reportPeriods;

        this.setState({
          reportPeriods: reportPeriods,
          defaultReportPeriod: defaultPeriod,
          // preserve selectedPeriod if already chosen, otherwise use default
          selectedPeriod: this.state.selectedPeriod || defaultPeriod,
          publishData: this.props.publishData.filter(
            (rp) =>
              rp.reportPeriodDescription ===
                (this.state.selectedPeriod || defaultPeriod) &&
              rp.isREITActive === true
          ),
        });
      });
  }

  /**
   * Handle report period dropdown change
   */
  handlePeriodChange = (reportPeriod) => {
    this.setState(
      {
        selectedPeriod: reportPeriod,
        defaultReportPeriod: reportPeriod,
      },
      () => {
        this.loadPublishData(reportPeriod);
      }
    );
  };

  handleSelectionChange = (rowData) => {
    const original =
      this.props.publishData.filter(
        (rp) =>
          rp.reportPeriodDescription ===
            (this.state.selectedPeriod || this.state.defaultReportPeriod) &&
          rp.isREITActive === true
      ) || [];
    const current = Array.isArray(rowData) ? rowData : [];

    // Build lookup for originals by reportPeriodID
    const origMap = new Map();
    original.forEach((o) => {
      const key = `${o.reportPeriodID}`;
      origMap.set(key, o);
    });

    // Find rows where selection changed compared to original
    const selectedPublishData = current.filter((r) => {
      const key = `${r.reportPeriodID}`;
      const orig = origMap.get(key);
      if (!orig) return false;
      return (
        Boolean(orig.isTestResultsDataPublished) !==
          Boolean(r.isTestResultsDataPublished) ||
        Boolean(orig.isPSQDataPublished) !== Boolean(r.isPSQDataPublished) ||
        Boolean(orig.isARQCDataPublished) !== Boolean(r.isARQCDataPublished)
      );
    });

    this.setState({
      isSelectionChanged: selectedPublishData.length > 0,
    });
  };

  handleCancel() {
    this.setState(
      {
        isSelectionChanged: false,
      },
      () => {
        {
          this.loadPublishData(
            this.state.selectedPeriod || this.state.defaultReportPeriod
          );
        }
      }
    );
  }

  /**
   * Handle submit button click to publish data
   */
  handleSubmit() {
    // Reload data after publishing
    this.loadPublishData();

    // Clear selections
    this.setState({
      selectedReits: {
        testResults: [],
        psq: [],
        arqc: [],
      },
    });
  }

  render() {
    const { reportPeriods, publishData, defaultReportPeriod } = this.state;
    const isAdmin = this.isAdminUser();
    const hasPeriods = Array.isArray(reportPeriods) && reportPeriods.length > 0;

    return (
      <div className="publish-data-container" style={{ padding: '20px 0' }}>
        <h2 data-testid="pghdrAnalytics">Analytics Reports</h2>

        <MotifTabNavigation variant="default">
          <Link
            key="viewReport"
            to={`/client/${this.props.clientId}/analytics/viewReport`}
            tabIndex="-1"
          >
            <MotifTabControl
              role="tab"
              aria-selected={false}
              data-testid="tbViewReport"
            >
              View Report
            </MotifTabControl>
          </Link>
          {isAdmin && (
            <Link
              key="managePublishedData"
              to={`/client/${this.props.clientId}/analytics/managePublishedData`}
              tabIndex="-1"
            >
              <MotifTabControl
                className="motif-active"
                role="tab"
                aria-selected={true}
                data-testid="tbManagePublishedData"
              >
                Manage Published Data
              </MotifTabControl>
            </Link>
          )}
        </MotifTabNavigation>

        {!hasPeriods ? (
          <div className="standardSpacingT20R4B20L4">
            No report periods are available for publishing to external checklist
            users.
          </div>
        ) : (
          <>
            <PublishDataHeader
              reportPeriods={reportPeriods}
              handlePeriodChange={this.handlePeriodChange}
              onCancel={this.handleCancel}
              onSubmit={this.handleSubmit}
              defaultReportPeriod={defaultReportPeriod}
              isSelectionChanged={this.state.isSelectionChanged}
            />

            <PublishDataTable
              publishData={publishData}
              handleSelectionChange={this.handleSelectionChange}
            />
          </>
        )}
      </div>
    );
  }
}

PublishDataContainer.propTypes = {
  clientId: PropTypes.number,
  router: PropTypes.object,
  currentUserAuthorizations: PropTypes.array,
  publishDataFormValues: PropTypes.object,
};

function mapStateToProps(state, ownProps) {
  let cid = Number.parseInt(ownProps.router.params.clientId);
  if (isNaN(cid)) {
    cid = 0;
  }

  return {
    currentUserAuthorizations: state.currentUserAuthorizations,
    clientId: cid,
    publishDataFormValues: getFormValues('publishDataForm')(state),
    publishData: state.publishData,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    // bind action creators here when needed
    publishDataActions: bindActionCreators(publishDataActions, dispatch),
  };
}

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(PublishDataContainer)
);
